// Growth › Previews: every preview built for a lead, a private ScoreLM league on
// its own address that deletes itself at its end. Search, status and paging
// live in the URL (?q=&status=&page=). A preview is made from its lead's page.

import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { api } from '@/api'
import { PREVIEW_STATUSES, type Preview, type PreviewStatus } from '@/api/types'
import { PageHeader } from '@/components/shell/PageHeader'
import { Button, ButtonLink, Card, DataTable, EmptyState, ErrorState, FilterChips, Pagination, RelativeTime, SearchInput, SkeletonRows, type Column } from '@/components/ui'
import { useDocumentTitle } from '@/lib/hooks'
import { formatDate } from '@/lib/format'
import { addressText, daysLeftText, PREVIEW_STATUS_LABELS } from '@/lib/previews'
import { useQuery } from '@/lib/query'
import { PreviewStatusBadge } from './previewShared'

const PAGE_SIZE = 25

const COLUMNS: Column<Preview>[] = [
  {
    id: 'name',
    header: 'League',
    primary: true,
    cell: (p) => (
      <span className="grid min-w-0 gap-0.5">
        <span className="break-words font-semibold">{p.name}</span>
        <span className="truncate text-xs font-medium text-muted">{addressText(p)}</span>
      </span>
    ),
  },
  { id: 'status', header: 'Status', cell: (p) => <PreviewStatusBadge preview={p} /> },
  { id: 'ends', header: 'Ends', cell: (p) => (p.status === 'active' ? <span>{formatDate(p.expires_at)} <span className="text-muted">({daysLeftText(p)})</span></span> : <span className="text-muted">{formatDate(p.expires_at)}</span>) },
  { id: 'views', header: 'Sign-ins', cell: (p) => p.view_count },
  {
    id: 'viewed',
    header: 'Last signed in',
    cell: (p) => (p.last_viewed_at ? <RelativeTime value={p.last_viewed_at} className="text-muted" /> : <span className="text-muted">Never</span>),
  },
]

export default function PreviewsListPage() {
  useDocumentTitle('Previews')
  const [searchParams, setSearchParams] = useSearchParams()
  const q = searchParams.get('q') ?? ''
  const rawStatus = searchParams.get('status')
  const status = (PREVIEW_STATUSES as readonly string[]).includes(rawStatus ?? '') ? (rawStatus as PreviewStatus) : undefined
  const rawPage = Number(searchParams.get('page'))
  const page = Number.isInteger(rawPage) && rawPage > 0 ? rawPage : 1

  const update = (patch: Record<string, string | number | undefined>) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        for (const [k, v] of Object.entries(patch)) {
          if (v === undefined || v === '' || (k === 'page' && v === 1)) next.delete(k)
          else next.set(k, String(v))
        }
        if (!('page' in patch)) next.delete('page')
        return next
      },
      { replace: true },
    )

  const params = useMemo(() => ({ q, status, page, page_size: PAGE_SIZE }), [q, status, page])
  const { data, error, status: loadStatus, isFetching, refetch } = useQuery(['growth', 'previews', params], (signal) => api.growth.previews.list(params, signal), { keepPrevious: true })

  const counts = data?.status_counts
  const rows = data?.previews ?? []
  const hasFilters = !!q || !!status

  return (
    <div className="grid grid-cols-1 gap-5">
      <PageHeader title="Previews" description="Private ScoreLM leagues built for leads. Each lead signs in with their own link, and the league deletes itself at its end." />
      <div className="grid gap-3">
        <SearchInput label="Search previews" placeholder="Search league, lead or address" value={q} onChange={(v) => update({ q: v })} className="sm:max-w-sm" />
        <FilterChips
          label="Filter by status"
          options={(['all', ...PREVIEW_STATUSES] as const).map((s) => ({
            value: s,
            label: s === 'all' ? 'All' : PREVIEW_STATUS_LABELS[s],
            count: counts ? (s === 'all' ? Object.values(counts).reduce((a, b) => a + b, 0) : counts[s]) : undefined,
          }))}
          value={status ?? 'all'}
          onChange={(v) => update({ status: v === 'all' ? undefined : v })}
        />
      </div>

      <Card flush className="overflow-hidden">
        {loadStatus === 'loading' ? (
          <SkeletonRows rows={5} label="Loading previews" />
        ) : loadStatus === 'error' && !data ? (
          <ErrorState error={error} title="Couldn't load previews" onRetry={refetch} />
        ) : rows.length === 0 ? (
          data && data.total > 0 ? (
            <EmptyState
              title="No results on this page"
              description={`Page ${page} is past the end.`}
              action={
                <Button size="sm" onClick={() => update({ page: 1 })}>
                  Go to page 1
                </Button>
              }
            />
          ) : hasFilters ? (
            <EmptyState
              title="No previews match"
              description="Try another search or filter."
              action={
                <Button size="sm" onClick={() => update({ q: undefined, status: undefined })}>
                  Clear filters
                </Button>
              }
            />
          ) : (
            <EmptyState
              title="No previews yet"
              description="Open a lead and choose Build a preview."
              action={
                <ButtonLink to="/growth/leads" size="sm">
                  Go to leads
                </ButtonLink>
              }
            />
          )
        ) : (
          <>
            {loadStatus === 'error' ? (
              <div role="alert" className="flex flex-wrap items-center justify-between gap-2 border-b border-line bg-bad-soft/40 px-4 py-2 text-xs text-bad">
                Couldn't refresh. Showing the last results.
                <Button size="sm" variant="ghost" onClick={refetch}>
                  Retry
                </Button>
              </div>
            ) : null}
            <DataTable caption="Previews" rows={rows} columns={COLUMNS} rowKey={(p) => p.id} rowHref={(p) => `/growth/previews/${p.id}`} busy={isFetching && loadStatus === 'success'} />
            <div className="border-t border-line px-4 py-3">
              <Pagination page={data?.page ?? page} pageSize={data?.page_size ?? PAGE_SIZE} total={data?.total ?? 0} noun="previews" onPageChange={(p) => update({ page: p })} />
            </div>
          </>
        )}
      </Card>
    </div>
  )
}
